import { useDispatch, useSelector } from 'react-redux';

import { ButtonSquare, Icon, RangeSlider } from '../../../shared/ui';
import { IconMap } from '../../../shared/sprite';
import { chatModel } from '../model';
import {
  ChatDocumentLanguage,
  ChatWritingStyle,
} from '@distribute/shared/types';
import { useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '../../../shared/ui/select/SelectV2';
import {
  CHAT_LANGUAGE_MAP,
  CHAT_WRITING_STYLE_MAP,
} from '@distribute/shared/chat';

import { ChatSettingsUpdateAction } from '../model/types';

export const ChatBotSettings = () => {
  const dispatch = useDispatch();

  const _documentLanguage = useSelector(
    chatModel.selectors.selectChatDocumentLanguage
  );
  const _temperature = useSelector(chatModel.selectors.selectChatTemperature);
  const _writingStyle = useSelector(chatModel.selectors.selectChatWritingStyle);

  const [documentLanguage, setDocumentLanguage] =
    useState<ChatDocumentLanguage>(_documentLanguage);
  const [writingStyle, setWritingStyle] =
    useState<ChatWritingStyle>(_writingStyle);
  const [temperature, setTemperature] = useState<number>(_temperature);

  const updateSettings = (updates: ChatSettingsUpdateAction) => {
    dispatch(
      chatModel.actions.updateChatSettings({
        writingStyle,
        temperature,
        documentLanguage,
        ...updates,
      })
    );
  };

  const handleLanguageChange = (value: ChatDocumentLanguage) => {
    setDocumentLanguage(value);
    updateSettings({ documentLanguage: value });
  };

  const handleWritingStyleChange = (value: ChatWritingStyle) => {
    setWritingStyle(value);
    updateSettings({ writingStyle: value });
  };

  const handleTemperatureChange = (value: number) => {
    setTemperature(value);
    updateSettings({ temperature: value });
  };

  return (
    <div className="flex flex-col gap-y-5">
      <div className="flex flex-nowrap justify-between items-center">
        <h3 className="text-2xl font-semibold text-gray-900">Settings</h3>
        <ButtonSquare
          variant="tertiary-gray"
          className="w-8 h-8 rounded-lg"
          onClick={() => dispatch(chatModel.actions.setScreen('main'))}
        >
          <Icon glyph={IconMap.XClose} width={20} />
        </ButtonSquare>
      </div>
      <div className="flex flex-col gap-y-3">
        <p className="text-sm text-gray-700 font-medium">Creativity</p>
        <div className="flex flex-nowrap items-center gap-x-3">
          <p className="text-sm text-gray-600">Low</p>
          <RangeSlider
            max={1}
            min={0}
            step={0.05}
            defaultValue={[temperature]}
            onChange={handleTemperatureChange}
          />
          <p className="text-sm text-gray-600">High</p>
        </div>
      </div>
      <div className="flex flex-col gap-y-3">
        <p className="text-sm text-gray-700 font-medium">Document language</p>
        <Select value={documentLanguage} onValueChange={handleLanguageChange}>
          <SelectTrigger className="text-md text-gray-800 font-medium h-11 shadow-xs">
            {CHAT_LANGUAGE_MAP[documentLanguage]}
          </SelectTrigger>
          <SelectContent side="bottom" align="end" sideOffset={4}>
            {Object.keys(CHAT_LANGUAGE_MAP).map((value) => (
              <SelectItem value={value} key={value}>
                {CHAT_LANGUAGE_MAP[value as ChatDocumentLanguage]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-col gap-y-3">
        <p className="text-sm text-gray-700 font-medium">Writing style</p>
        <Select value={writingStyle} onValueChange={handleWritingStyleChange}>
          <SelectTrigger className="text-md text-gray-800 font-medium h-11 shadow-xs">
            {CHAT_WRITING_STYLE_MAP[writingStyle]}
          </SelectTrigger>
          <SelectContent side="bottom" align="end" sideOffset={4}>
            {Object.keys(CHAT_WRITING_STYLE_MAP).map((value) => (
              <SelectItem value={value} key={value}>
                {CHAT_WRITING_STYLE_MAP[value as ChatWritingStyle]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};
